import {
  Component,
  type ErrorInfo,
  type PropsWithChildren,
  type ReactNode,
} from "react";

import { faro } from "@grafana/faro-react";
import { LogLevel } from "@grafana/faro-web-sdk";

import { PrivatePageError } from "./PrivatePageError";
import { PublicPageError } from "./PublicPageError";

type Props = PropsWithChildren & {
  isPublicPage: boolean;
  locationKey: string | null | undefined;
};

type State = {
  hasError: boolean;
};

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { isPublicPage, locationKey } = this.props;

    faro.api.pushError(error, {
      type: "react-error-boundary",
      context: {
        componentStack: errorInfo.componentStack ?? "",
        message: "Critical error! Page is not visible for a customer.",
        isPublicPage: String(isPublicPage),
        ...(locationKey != null && { locationKey }),
      },
    });

    faro.api.pushLog(
      [`[Crash] React error boundary caught: ${error.message}`],
      {
        level: LogLevel.ERROR,
        context: {
          type: "crash.page",
          source: "react-error-boundary",
          sessionId: faro.api.getSession()?.id ?? "unknown",
          isPublicPage: String(isPublicPage),
          pathname: window.location.pathname,
          componentStack: errorInfo.componentStack ?? "",
        },
      },
    );
  }

  componentDidUpdate(previousProps: Props): void {
    const { locationKey } = this.props;
    const { hasError } = this.state;

    if (hasError && previousProps.locationKey !== locationKey) {
      this.setState({ hasError: false });
    }
  }

  render(): ReactNode {
    const { children, isPublicPage } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      return isPublicPage ? <PublicPageError /> : <PrivatePageError />;
    }

    return children;
  }
}
